import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  addDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";
import { convertToMonths, formatDuration } from "../utils/warrantyUtils";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: "90vh",
  overflow: "auto",
};

const WarrantyTypeModal = ({ open, onClose, clientId, selectedProperty, selectedType, userInfo }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationValue, setDurationValue] = useState(1);
  const [durationUnit, setDurationUnit] = useState("months");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedType) {
      setName(selectedType.name || "");
      setDescription(selectedType.description || "");
      setIsActive(selectedType.isActive !== undefined ? selectedType.isActive : true);

      // Convert months to display format
      const formatted = formatDuration(selectedType.durationMonths || 0);
      setDurationValue(formatted.value);
      setDurationUnit(formatted.unit);
    } else {
      // Reset for new type
      setName("");
      setDescription("");
      setDurationValue(1);
      setDurationUnit("months");
      setIsActive(true);
    }
    setError("");
  }, [selectedType, open]);

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      setError("Warranty type name is required");
      return;
    }

    if (!durationValue || durationValue <= 0) {
      setError("Duration must be greater than 0");
      return;
    }

    // Convert duration to months
    const durationMonths = convertToMonths(durationValue, durationUnit);

    const warrantyTypeData = {
      name: name.trim(),
      description: description.trim(),
      durationMonths,
      isActive,
      lastModified: serverTimestamp(),
    };

    try {
      const warrantyTypesRef = propertyCollection(
        clientId,
        selectedProperty,
        "warrantyTypes"
      );

      if (selectedType && selectedType.id) {
        // Update existing
        const typeDoc = doc(warrantyTypesRef, selectedType.id);
        await updateDoc(typeDoc, warrantyTypeData);
      } else {
        // Create new
        await addDoc(warrantyTypesRef, {
          ...warrantyTypeData,
          createdAt: serverTimestamp(),
          createdBy: userInfo?.id || "unknown",
        });
      }

      // Clean up and close
      setName("");
      setDescription("");
      setDurationValue(1);
      setDurationUnit("months");
      setIsActive(true);
      setError("");
      onClose();
    } catch (err) {
      console.error("Error saving warranty type:", err);
      setError("Failed to save warranty type. Please try again.");
    }
  };

  const handleClose = () => {
    setError("");
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {selectedType ? "Edit Warranty Type" : "Add Warranty Type"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Warranty Type Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Refrigerator - 1 Year"
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Duration"
            type="number"
            value={durationValue}
            onChange={(e) => setDurationValue(Number(e.target.value))}
            inputProps={{ min: 1 }}
            sx={{ flex: 1 }}
          />

          <FormControl sx={{ flex: 1 }}>
            <InputLabel>Unit</InputLabel>
            <Select
              value={durationUnit}
              label="Unit"
              onChange={(e) => setDurationUnit(e.target.value)}
            >
              <MenuItem value="days">Days</MenuItem>
              <MenuItem value="months">Months</MenuItem>
              <MenuItem value="years">Years</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TextField
          fullWidth
          label="Description (Optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          placeholder="Additional notes about this warranty type"
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          }
          label="Active (available for use)"
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            {selectedType ? "Update" : "Create"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const WarrantyTypes = ({ clientId, selectedProperty, userInfo }) => {
  const { selectedProperty: contextProperty, selectedClient: contextClient } = useContext(PropertyContext);
  const property = selectedProperty || contextProperty;
  const client = clientId || contextClient;

  const [warrantyTypes, setWarrantyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Real-time listener for warranty types
  useEffect(() => {
    if (!property) {
      setLoading(false);
      return;
    }

    const warrantyTypesRef = propertyCollection(
      client,
      property,
      "warrantyTypes"
    );
    const q = query(warrantyTypesRef, orderBy("name"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const types = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setWarrantyTypes(types);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching warranty types:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [property]);

  const handleAddClick = () => {
    setSelectedType(null);
    setShowModal(true);
  };

  const handleEditClick = (type) => {
    setSelectedType(type);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedType(null);
  };

  const handleToggleActive = async (type) => {
    try {
      const typeDoc = propertyDoc(
        client,
        property,
        "warrantyTypes",
        type.id
      );
      await updateDoc(typeDoc, {
        isActive: !type.isActive,
        lastModified: serverTimestamp(),
      });

      setSnackbar({
        open: true,
        message: `Warranty type ${!type.isActive ? "activated" : "deactivated"}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error toggling warranty type:", error);
      setSnackbar({
        open: true,
        message: "Failed to update warranty type",
        severity: "error",
      });
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Warranty Type Name",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => {
        const formatted = formatDuration(params.row.durationMonths || 0);
        return formatted.display;
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Typography
          sx={{
            color: params.row.isActive ? "success.main" : "text.disabled",
            fontWeight: 500,
          }}
        >
          {params.row.isActive ? "Active" : "Inactive"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleEditClick(params.row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color={params.row.isActive ? "warning" : "success"}
            onClick={() => handleToggleActive(params.row)}
          >
            {params.row.isActive ? "Deactivate" : "Activate"}
          </Button>
        </Box>
      ),
    },
  ];

  if (!property) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Please select a property to manage warranty types.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">Warranty Types</Typography>
        <Button variant="contained" onClick={handleAddClick}>
          Add Warranty Type
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Warranty types are templates that can be reused when adding warranty items.
        Create common durations (30 days, 1 year, 5 years) to speed up data entry.
      </Alert>

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={warrantyTypes}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Box>

      <WarrantyTypeModal
        open={showModal}
        onClose={handleModalClose}
        clientId={client}
        selectedProperty={property}
        selectedType={selectedType}
        userInfo={userInfo}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WarrantyTypes;
